import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Spinner, Alert, Table } from 'react-bootstrap';
import { getAuthSession } from 'features/auth/authSession';
import { useAuth } from 'providers/AuthProvider';
import { apiConfig } from 'config/apiConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faHospital,
  faCalendar,
  faClipboardCheck,
  faRightFromBracket,
  faCheckCircle,
  faClock,
  faXmark,
  faCheckDouble
} from '@fortawesome/free-solid-svg-icons';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { PieChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from 'providers/AppProvider';

echarts.use([
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  PieChart,
  CanvasRenderer
]);

interface DashboardOverview {
  totalLocums: number;
  totalPharmacists: number;
  totalShifts: number;
  totalBookings: number;
}

interface BookingStatus {
  status: string;
  count: number;
}

interface RecentBooking {
  id: number;
  status: string;
  created_at: string;
  locum_name: string;
  pharmacy_name: string;
  shift_date: string;
  start_time: string;
  end_time: string;
}

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { getThemeColor } = useAppContext();
  const { logout } = useAuth();
  const [adminData, setAdminData] = useState<any>(null);
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [bookingStatus, setBookingStatus] = useState<BookingStatus[]>([]);
  const [recentBookings, setRecentBookings] = useState<RecentBooking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const session = getAuthSession();
        if (!session) {
          navigate('/auth/login');
          return;
        }

        setAdminData(session.user);

        // Fetch all dashboard data
        const [overviewRes, statusRes, bookingsRes] = await Promise.all([
          fetch(
            apiConfig.getApiUrl(apiConfig.endpoints.admin.dashboard.overview),
            { headers: { Authorization: `Bearer ${session.token}` } }
          ),
          fetch(
            apiConfig.getApiUrl(apiConfig.endpoints.admin.dashboard.bookingStatus),
            { headers: { Authorization: `Bearer ${session.token}` } }
          ),
          fetch(
            apiConfig.getApiUrl(apiConfig.endpoints.admin.dashboard.recentBookings),
            { headers: { Authorization: `Bearer ${session.token}` } }
          )
        ]);

        if (!overviewRes.ok || !statusRes.ok || !bookingsRes.ok) {
          throw new Error('Failed to fetch data');
        }

        const overviewData = await overviewRes.json();
        const statusData = await statusRes.json();
        const bookingsData = await bookingsRes.json();

        if (overviewData.success) setOverview(overviewData.data);
        if (statusData.success) setBookingStatus(statusData.data);
        if (bookingsData.success) setRecentBookings(bookingsData.data);
      } catch (err: any) {
        setError(err.message || 'Failed to load dashboard');
        console.error('Failed to fetch data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/auth/admin-login', { replace: true });
  };

  const getBookingStatusColor = (status: string): string => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return '#198754';
      case 'completed':
        return '#0d6efd';
      case 'pending':
        return '#ffc107';
      case 'cancelled':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  const getPieChartOptions = () => {
    const colors = bookingStatus.map(item => getBookingStatusColor(item.status));
    const data = bookingStatus.map(item => ({
      value: item.count,
      name: item.status?.charAt(0).toUpperCase() + item.status?.slice(1)
    }));

    return {
      color: colors,
      tooltip: {
        trigger: 'item',
        backgroundColor: getThemeColor('body-highlight-bg'),
        borderColor: getThemeColor('border-color'),
        textStyle: { color: getThemeColor('light-text-emphasis') },
        borderWidth: 1
      },
      legend: {
        orient: 'horizontal',
        bottom: 0,
        textStyle: { color: getThemeColor('secondary-color') },
        itemGap: 20
      },
      series: [
        {
          name: 'Bookings',
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: { borderColor: getThemeColor('body-bg'), borderWidth: 3 },
          label: { show: false },
          emphasis: { label: { show: true } },
          data
        }
      ]
    };
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return 'bg-success';
      case 'completed':
        return 'bg-info';
      case 'pending':
        return 'bg-warning';
      case 'cancelled':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return faCheckCircle;
      case 'completed':
        return faCheckDouble;
      case 'pending':
        return faClock;
      case 'cancelled':
        return faXmark;
      default:
        return faClock;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container className="py-2 py-lg-4">
      {/* Header Section */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div>
              <p className="text-body-tertiary text-uppercase fs-10 fw-bold mb-1">
                Administration
              </p>
              <h2 className="text-body-emphasis mb-1">Dashboard</h2>
              <p className="mb-0 text-body-tertiary fs-9">
                Welcome back, <span className="fw-semibold">{adminData?.name || 'Admin'}</span>
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="btn btn-outline-danger d-inline-flex align-items-center gap-2"
              title="Logout"
            >
              <FontAwesomeIcon icon={faRightFromBracket} size="sm" />
              <span>Logout</span>
            </button>
          </div>
        </Col>
      </Row>

      {/* Error Alert */}
      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger" className="mb-0">
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Dashboard Overview Stats */}
      {!isLoading && overview && (
        <Row className="g-3 mb-5">
          {/* Total Locums */}
          <Col xs={12} sm={6} lg={3}>
            <div className="p-4 rounded-3 bg-body border border-translucent h-100 transition-all hover-card">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <small className="text-body-tertiary text-uppercase fw-bold fs-10">
                  Total Locums
                </small>
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle shadow-primary-100"
                  style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: 'rgba(13, 110, 253, 0.1)'
                  }}
                >
                  <FontAwesomeIcon
                    icon={faUsers}
                    className="text-primary"
                    size="lg"
                  />
                </div>
              </div>
              <h3 className="mb-2 fw-bold text-primary display-6">
                {overview.totalLocums}
              </h3>
              <p className="mb-0 fs-9 text-body-tertiary">Active locum professionals</p>
            </div>
          </Col>

          {/* Total Pharmacists */}
          <Col xs={12} sm={6} lg={3}>
            <div className="p-4 rounded-3 bg-body border border-translucent h-100 transition-all hover-card">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <small className="text-body-tertiary text-uppercase fw-bold fs-10">
                  Total Pharmacy
                </small>
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle shadow-success-100"
                  style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: 'rgba(25, 135, 84, 0.1)'
                  }}
                >
                  <FontAwesomeIcon
                    icon={faHospital}
                    className="text-success"
                    size="lg"
                  />
                </div>
              </div>
              <h3 className="mb-2 fw-bold text-success display-6">
                {overview.totalPharmacists}
              </h3>
              <p className="mb-0 fs-9 text-body-tertiary">Active pharmacy partners</p>
            </div>
          </Col>

          {/* Total Shifts */}
          <Col xs={12} sm={6} lg={3}>
            <div className="p-4 rounded-3 bg-body border border-translucent h-100 transition-all hover-card">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <small className="text-body-tertiary text-uppercase fw-bold fs-10">
                  Total Shifts
                </small>
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle shadow-warning-100"
                  style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: 'rgba(255, 193, 7, 0.1)'
                  }}
                >
                  <FontAwesomeIcon
                    icon={faCalendar}
                    className="text-warning"
                    size="lg"
                  />
                </div>
              </div>
              <h3 className="mb-2 fw-bold text-warning display-6">
                {overview.totalShifts}
              </h3>
              <p className="mb-0 fs-9 text-body-tertiary">Available positions</p>
            </div>
          </Col>

          {/* Total Bookings */}
          <Col xs={12} sm={6} lg={3}>
            <div className="p-4 rounded-3 bg-body border border-translucent h-100 transition-all hover-card">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <small className="text-body-tertiary text-uppercase fw-bold fs-10">
                  Total Bookings
                </small>
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle shadow-info-100"
                  style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: 'rgba(23, 162, 184, 0.1)'
                  }}
                >
                  <FontAwesomeIcon
                    icon={faClipboardCheck}
                    className="text-info"
                    size="lg"
                  />
                </div>
              </div>
              <h3 className="mb-2 fw-bold text-info display-6">
                {overview.totalBookings}
              </h3>
              <p className="mb-0 fs-9 text-body-tertiary">All assignments</p>
            </div>
          </Col>
        </Row>
      )}

      {/* Charts and Recent Activity Section */}
      <Row className="g-4">
        {/* Booking Status Chart */}
        {bookingStatus.length > 0 && (
          <Col lg={6}>
            <div className="p-4 rounded-3 bg-body border border-translucent h-100">
              <h5 className="text-body-emphasis fw-bold mb-4">Booking Status Distribution</h5>
              <div style={{ height: '300px' }}>
                <ReactEChartsCore
                  echarts={echarts}
                  option={getPieChartOptions()}
                  style={{ height: '100%', width: '100%' }}
                />
              </div>
            </div>
          </Col>
        )}

        {/* Recent Bookings */}
        <Col lg={bookingStatus.length > 0 ? 6 : 12}>
          <div className="p-4 rounded-3 bg-body border border-translucent h-100">
            <h5 className="text-body-emphasis fw-bold mb-4">Recent Bookings</h5>
            {recentBookings.length > 0 ? (
              <div className="table-responsive">
                <Table hover className="mb-0" size="sm">
                  <thead className="table-light">
                    <tr>
                      <th className="border-top-0 ps-0">Locum</th>
                      <th className="border-top-0">Pharmacy</th>
                      <th className="border-top-0">Date</th>
                      <th className="border-top-0 text-end pe-0">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentBookings.map(booking => (
                      <tr key={booking.id}>
                        <td className="ps-0 fw-semibold">{booking.locum_name}</td>
                        <td className="fs-9">{booking.pharmacy_name}</td>
                        <td className="fs-9">{formatDate(booking.shift_date)}</td>
                        <td className="text-end pe-0">
                          <span
                            className={`badge ${getStatusBadgeClass(
                              booking.status
                            )} px-2`}
                          >
                            <FontAwesomeIcon
                              icon={getStatusIcon(booking.status)}
                              size="xs"
                              className="me-1"
                            />
                            {booking.status?.charAt(0).toUpperCase() +
                              booking.status?.slice(1)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <p className="text-muted text-center mb-0">No recent bookings</p>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminDashboardPage;
